import {
	collection,
	doc,
	getDocs,
	getDoc,
	addDoc,
	updateDoc,
	deleteDoc,
	setDoc,
	query,
	where,
	orderBy,
	limit,
	startAfter,
	documentId,
	runTransaction,
	writeBatch,
	Timestamp,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { AutoIncrementService } from '@/v2/agents/helpers/auto-increment-service';
import { Product } from './entities/product';
import { Discount } from './entities/discount';
import { getTenantCollectionPath } from './utils/firebase';
import { CategoryFunctions } from './functions/categories';
import { BrandFunctions } from './functions/brand';
import { OptionFunctions } from './functions/options';
import { TagFunctions } from './functions/tags';
import { callFirebaseFunction } from '@/v2/utils/firebase';
import { Category } from './entities/category';
import { Brand } from './entities/brand';
import { Option } from './entities/option';
import { Tag } from './entities/tag';
import axios from 'axios';
import { useAuthStore } from '@/v2/store/shared/auth-store';
import { MyFatoorah } from './entities/my-fatoorah';
import { Order, OrderStatus, PaymentMethod } from './entities/order';
import { InvoiceSettings } from './entities/invoice-settings';
import { AgentSettings } from './entities/agent-settings';
import { normalizeHomepageSections } from './entities/homepage-sections';

export class AgentEcommerceService {
	static async getGeneralSettings() {
		const settingsRef = collection(db, getTenantCollectionPath('settings'));
		const settingsDoc = await getDoc(doc(settingsRef, 'general'));
		return settingsDoc.data();
	}

	// Ecommerce General Settings (for General Settings page)
	static async getEcommerceGeneralSettings() {
		try {
			// Get COD Fees from shipping/settings
			const shippingSettingsRef = doc(db, getTenantCollectionPath('shipping'), 'settings');
			const shippingSettingsDoc = await getDoc(shippingSettingsRef);
			const cashOnDeliveryPrice = shippingSettingsDoc.exists()
				? shippingSettingsDoc.data().cashOnDeliveryPrice ?? 0
				: 0;

			// Get Order Cancellation Window Hours from settings/general
			const generalSettingsRef = doc(db, getTenantCollectionPath('settings'), 'general');
			const generalSettingsDoc = await getDoc(generalSettingsRef);
			const cancellationWindowHours = generalSettingsDoc.exists()
				? generalSettingsDoc.data().cancellationWindowHours ?? 1
				: 1;

			return {
				cashOnDeliveryPrice,
				cancellationWindowHours,
			};
		} catch (error) {
			console.error('Error fetching ecommerce general settings:', error);
			throw error;
		}
	}

	static async updateEcommerceGeneralSettings(settings) {
		try {
			const batch = writeBatch(db);

			// Update COD Fees in shipping/settings
			const shippingSettingsRef = doc(db, getTenantCollectionPath('shipping'), 'settings');
			batch.set(
				shippingSettingsRef,
				{
					cashOnDeliveryPrice: settings.cashOnDeliveryPrice ?? 0,
					updatedAt: new Date(),
				},
				{ merge: true },
			);

			// Update Order Cancellation Window Hours in settings/general
			const generalSettingsRef = doc(db, getTenantCollectionPath('settings'), 'general');
			batch.set(
				generalSettingsRef,
				{
					cancellationWindowHours: settings.cancellationWindowHours ?? 1,
					updatedAt: new Date(),
				},
				{ merge: true },
			);

			await batch.commit();
		} catch (error) {
			console.error('Error updating ecommerce general settings:', error);
			throw error;
		}
	}

	// Storefront homepage sections (visibility + order) -> settings/homepage
	static async getHomepageSections() {
		const settingsRef = doc(db, getTenantCollectionPath('settings'), 'homepage');
		const settingsDoc = await getDoc(settingsRef);
		return normalizeHomepageSections(settingsDoc.exists() ? settingsDoc.data().sections : null);
	}

	static async updateHomepageSections(sections) {
		const settingsRef = doc(db, getTenantCollectionPath('settings'), 'homepage');
		await setDoc(
			settingsRef,
			{
				sections: normalizeHomepageSections(sections).map(({ key, visible }) => ({
					key,
					visible: Boolean(visible),
				})),
				updatedAt: new Date(),
			},
			{ merge: true },
		);
	}

	// Shipping Configuration Settings
	static async getShippingSettings() {
		try {
			const generalSettingsRef = doc(db, getTenantCollectionPath('settings'), 'general');
			const generalSettingsDoc = await getDoc(generalSettingsRef);

			if (generalSettingsDoc.exists()) {
				const data = generalSettingsDoc.data();
				return {
					supportPickupFromBranch: data.supportPickupFromBranch ?? false,
				};
			}

			// Return defaults if document doesn't exist
			return {
				supportPickupFromBranch: false,
			};
		} catch (error) {
			console.error('Error fetching shipping settings:', error);
			throw error;
		}
	}

	static async updateShippingSettings(settings) {
		try {
			const generalSettingsRef = doc(db, getTenantCollectionPath('settings'), 'general');
			await setDoc(
				generalSettingsRef,
				{
					supportPickupFromBranch: settings.supportPickupFromBranch ?? false,
					updatedAt: new Date(),
				},
				{ merge: true },
			);
		} catch (error) {
			console.error('Error updating shipping settings:', error);
			throw error;
		}
	}

	// Payment Methods Settings
	static async getPaymentMethodsSettings() {
		try {
			const generalSettingsRef = doc(db, getTenantCollectionPath('settings'), 'general');
			const generalSettingsDoc = await getDoc(generalSettingsRef);

			const shippingSettingsRef = doc(db, getTenantCollectionPath('shipping'), 'settings');
			const shippingSettingsDoc = await getDoc(shippingSettingsRef);

			const paymentMethods = generalSettingsDoc.exists()
				? generalSettingsDoc.data().paymentMethods || {
						bank_transfer: false,
						cash_on_delivery: false,
						my_fatoorah: false,
				  }
				: {
						bank_transfer: false,
						cash_on_delivery: false,
						my_fatoorah: false,
				  };

			const bankAccountInfo = generalSettingsDoc.exists()
				? generalSettingsDoc.data().bankAccountInfo || {
						accountName: '',
						accountNumber: '',
						bankName: '',
						iban: '',
				  }
				: {
						accountName: '',
						accountNumber: '',
						bankName: '',
						iban: '',
				  };

			const cashOnDeliveryPrice = shippingSettingsDoc.exists()
				? shippingSettingsDoc.data().cashOnDeliveryPrice ?? 0
				: 0;

			return {
				paymentMethods,
				bankAccountInfo,
				cashOnDeliveryPrice,
			};
		} catch (error) {
			console.error('Error fetching payment methods settings:', error);
			throw error;
		}
	}

	static async updatePaymentMethodsSettings({
		paymentMethods,
		bankAccountInfo,
		cashOnDeliveryPrice,
	}) {
		try {
			const batch = writeBatch(db);

			// Update payment methods and bank account info in settings/general
			const generalSettingsRef = doc(db, getTenantCollectionPath('settings'), 'general');
			batch.set(
				generalSettingsRef,
				{
					paymentMethods: paymentMethods || {
						bank_transfer: false,
						cash_on_delivery: false,
						my_fatoorah: false,
					},
					bankAccountInfo: bankAccountInfo || {
						accountName: '',
						accountNumber: '',
						bankName: '',
						iban: '',
					},
					updatedAt: new Date(),
				},
				{ merge: true },
			);

			// Update COD fees in shipping/settings
			const shippingSettingsRef = doc(db, getTenantCollectionPath('shipping'), 'settings');
			batch.set(
				shippingSettingsRef,
				{
					cashOnDeliveryPrice: cashOnDeliveryPrice ?? 0,
					updatedAt: new Date(),
				},
				{ merge: true },
			);

			await batch.commit();
		} catch (error) {
			console.error('Error updating payment methods settings:', error);
			throw error;
		}
	}

	// Brands operations
	static async getBrands({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const brandsRef = collection(db, getTenantCollectionPath('brands', db));
			let q = query(brandsRef, orderBy('name'));

			if (search) {
				q = query(
					brandsRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const brands = snapshot.docs.map((doc) => new Brand({ id: doc.id, ...doc.data() }));

			return {
				data: brands,
				totalRecords: brands.length,
			};
		} catch (error) {
			console.error('Error fetching brands:', error);
			throw error;
		}
	}

	static async getBrand(id) {
		try {
			const brandDoc = await getDoc(doc(db, getTenantCollectionPath('brands'), id));
			if (brandDoc.exists()) {
				return new Brand({ id: brandDoc.id, ...brandDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching brand:', error);
			throw error;
		}
	}

	static async createBrand(brandData) {
		try {
			const nextId = await AutoIncrementService.getNextId('brands', db);
			await setDoc(doc(db, getTenantCollectionPath('brands'), nextId.toString()), {
				...brandData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating brand:', error);
			throw error;
		}
	}

	static async updateBrand(id, brandData) {
		try {
			const oldBrand = await this.getBrand(id);
			await updateDoc(doc(db, getTenantCollectionPath('brands'), id), {
				...brandData,
				updatedAt: new Date(),
			});

			if (!oldBrand.isSame(new Brand({ id, ...brandData }))) {
				BrandFunctions.updateBrand(id, brandData);
			}
		} catch (error) {
			console.error('Error updating brand:', error);
			throw error;
		}
	}

	static async deleteBrand(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('brands'), id));
		} catch (error) {
			console.error('Error deleting brand:', error);
			throw error;
		}
	}

	// Categories operations
	static async getCategories({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const categoriesRef = collection(db, getTenantCollectionPath('categories'));
			let q = query(categoriesRef, orderBy('name'));

			if (search) {
				q = query(
					categoriesRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const categories = snapshot.docs.map((doc) => new Category({ id: doc.id, ...doc.data() }));

			return {
				data: categories,
				totalRecords: categories.length,
			};
		} catch (error) {
			console.error('Error fetching categories:', error);
			throw error;
		}
	}

	static async getCategory(id) {
		try {
			const categoryDoc = await getDoc(doc(db, getTenantCollectionPath('categories'), id));
			if (categoryDoc.exists()) {
				return new Category({ id: categoryDoc.id, ...categoryDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching category:', error);
			throw error;
		}
	}

	static async createCategory(categoryData) {
		try {
			const nextId = await AutoIncrementService.getNextId('categories', db);
			await setDoc(doc(db, getTenantCollectionPath('categories'), nextId.toString()), {
				...categoryData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating category:', error);
			throw error;
		}
	}

	static async updateCategory(id, categoryData) {
		try {
			const oldCategory = await this.getCategory(id);
			await updateDoc(doc(db, getTenantCollectionPath('categories'), id), {
				...categoryData,
				updatedAt: new Date(),
			});

			if (!oldCategory.isSame(new Category({ id, ...categoryData }))) {
				CategoryFunctions.updateCategory(id, categoryData);
			}
		} catch (error) {
			console.error('Error updating category:', error);
			throw error;
		}
	}

	static async deleteCategory(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('categories'), id));
		} catch (error) {
			console.error('Error deleting category:', error);
			throw error;
		}
	}

	static async isCategoryUsedInProducts(categoryId) {
		try {
			const productsRef = collection(db, getTenantCollectionPath('products'));
			const snapshot = await getDocs(productsRef);

			if (snapshot.empty) {
				return false;
			}

			for (const productDoc of snapshot.docs) {
				const productData = productDoc.data();

				if (productData.categories && Array.isArray(productData.categories)) {
					const hasCategory = productData.categories.some((category) => {
						if (category && typeof category === 'object' && category.id) {
							return String(category.id) === String(categoryId);
						}
						return false;
					});

					if (hasCategory) {
						return true;
					}
				}
			}

			return false;
		} catch (error) {
			console.error('Error checking if category is used in products:', error);
			throw error;
		}
	}

	// Products operations
	static async getProducts({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const productsRef = collection(db, getTenantCollectionPath('products'));
			let q = query(productsRef, orderBy('name'));

			if (search) {
				q = query(
					productsRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const products = snapshot.docs.map((doc) => new Product({ id: doc.id, ...doc.data() }));

			return {
				data: products,
				totalRecords: products.length,
			};
		} catch (error) {
			console.error('Error fetching products:', error);
			throw error;
		}
	}

	static async getProductsByIds(ids) {
		try {
			const productsRef = collection(db, getTenantCollectionPath('products'));
			const snapshot = await getDocs(query(productsRef, where(documentId(), 'in', ids)));
			return snapshot.docs.map((doc) => new Product({ id: doc.id, ...doc.data() }));
		} catch (error) {
			console.error('Error fetching products by IDs:', error);
			throw error;
		}
	}

	static async getProductsAndVariantsByIds(discountProducts) {
		const products = await this.getProductsByIds(discountProducts.map((p) => p.id));
		return Product.mapProductsAndVariants(products, discountProducts);
	}

	static async getProduct(id) {
		try {
			const productDoc = await getDoc(doc(db, getTenantCollectionPath('products'), id));
			if (productDoc.exists()) {
				return new Product({ id: productDoc.id, ...productDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching product:', error);
			throw error;
		}
	}

	static async createProduct(productData) {
		try {
			const nextId = await AutoIncrementService.getNextId('products', db);

			if (productData.variants && Array.isArray(productData.variants)) {
				for (const variant of productData.variants) {
					if (!variant.id) {
						variant.id = String(await AutoIncrementService.getNextId('product_variants', db));
					}
				}
			}

			await setDoc(doc(db, getTenantCollectionPath('products'), nextId.toString()), {
				...productData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating product:', error);
			throw error;
		}
	}

	static async updateProduct(id, productData) {
		try {
			if (productData.variants && Array.isArray(productData.variants)) {
				for (const variant of productData.variants) {
					if (!variant.id) {
						variant.id = String(await AutoIncrementService.getNextId('product_variants', db));
					}
				}
			}

			await updateDoc(doc(db, getTenantCollectionPath('products'), id), {
				...productData,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating product:', error);
			throw error;
		}
	}

	static async deleteProduct(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('products'), id));
		} catch (error) {
			console.error('Error deleting product:', error);
			throw error;
		}
	}

	// Orders operations
	static async getOrders({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const ordersRef = collection(db, getTenantCollectionPath('orders'));
			let q = query(ordersRef, orderBy('createdAt', 'desc'));

			const snapshot = await getDocs(q);
			const orders = snapshot.docs.map((doc) => new Order({ id: doc.id, ...doc.data() }));

			return {
				data: orders,
				totalRecords: orders.length,
			};
		} catch (error) {
			console.error('Error fetching orders:', error);
			throw error;
		}
	}

	static async getOrder(id) {
		try {
			const orderDoc = await getDoc(doc(db, getTenantCollectionPath('orders'), id));
			if (orderDoc.exists()) {
				return new Order({ id: orderDoc.id, ...orderDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching order:', error);
			throw error;
		}
	}

	static async updateOrderStatus(id, status) {
		try {
			const order = await this.getOrder(id);

			await Order.changeOrderStatus({ orderId: id, status, customerId: order.customerId });
		} catch (error) {
			console.error('Error updating order status:', error);
			throw error;
		}
	}

	static async updateOrderPaymentStatus(id, paymentStatus, paymentMethod) {
		try {
			let supplierCode = null;

			if (paymentMethod === PaymentMethod.MyFatoorah) {
				const supplier = await MyFatoorah.getSupplier();
				if (!supplier) {
					throw new Error('Supplier not found');
				}

				supplierCode = supplier.supplier_code;
			}

			await Order.changePaymentStatus({
				orderId: id,
				paymentStatus,
				supplierCode: supplierCode,
			});
		} catch (error) {
			console.error('Error updating order payment status:', error);
			throw error;
		}
	}

	static async updateOrder(id, orderData) {
		try {
			await updateDoc(doc(db, getTenantCollectionPath('orders'), id), {
				...orderData,
				origin: 'webapp',
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating order:', error);
			throw error;
		}
	}

	static async hasDraftOrder(customerId) {
		const ordersRef = collection(db, getTenantCollectionPath('orders'));
		const q = query(
			ordersRef,
			where('customerId', '==', customerId),
			where('status', '==', OrderStatus.Draft),
		);
		const snapshot = await getDocs(q);
		return !snapshot.empty;
	}

	// Stores operations
	static async getStores({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const storesRef = collection(db, getTenantCollectionPath('stores'));
			let q = query(storesRef, orderBy('name'));

			if (search) {
				q = query(
					storesRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const stores = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));

			return {
				data: stores,
				totalRecords: stores.length,
			};
		} catch (error) {
			console.error('Error fetching stores:', error);
			throw error;
		}
	}

	static async getStore(id) {
		try {
			const storeDoc = await getDoc(doc(db, getTenantCollectionPath('stores'), id));
			if (storeDoc.exists()) {
				return { id: storeDoc.id, ...storeDoc.data() };
			}
			return null;
		} catch (error) {
			console.error('Error fetching store:', error);
			throw error;
		}
	}

	static async createStore(storeData) {
		try {
			// If this store is being set as default, first set all other stores to not default
			if (storeData.isDefault) {
				await this.unsetAllDefaultStores();
			}

			const nextId = await AutoIncrementService.getNextId('stores', db);
			await setDoc(doc(db, getTenantCollectionPath('stores'), nextId.toString()), {
				...storeData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating store:', error);
			throw error;
		}
	}

	static async updateStore(id, storeData) {
		try {
			// If this store is being set as default, first set all other stores to not default
			if (storeData.isDefault) {
				await this.unsetAllDefaultStores();
			}

			await updateDoc(doc(db, getTenantCollectionPath('stores'), id), {
				...storeData,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating store:', error);
			throw error;
		}
	}

	// Helper method to unset default flag from all stores
	static async unsetAllDefaultStores() {
		try {
			const storesRef = collection(db, getTenantCollectionPath('stores'));
			const q = query(storesRef, where('isDefault', '==', true));
			const snapshot = await getDocs(q);

			const updatePromises = snapshot.docs.map((doc) =>
				updateDoc(doc.ref, { isDefault: false, updatedAt: new Date() }),
			);

			await Promise.all(updatePromises);
		} catch (error) {
			console.error('Error unsetting default stores:', error);
			throw error;
		}
	}

	static async deleteStore(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('stores'), id));
		} catch (error) {
			console.error('Error deleting store:', error);
			throw error;
		}
	}

	// Discounts operations
	static async getDiscounts({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const discountsRef = collection(db, getTenantCollectionPath('discounts'));
			let q = query(discountsRef, orderBy('title'));

			if (search) {
				q = query(
					discountsRef,
					where('title', '>=', search),
					where('title', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const discounts = snapshot.docs.map((doc) => new Discount({ id: doc.id, ...doc.data() }));

			return {
				data: discounts,
				totalRecords: discounts.length,
			};
		} catch (error) {
			console.error('Error fetching discounts:', error);
			throw error;
		}
	}

	static async getDiscount(id) {
		try {
			const discountDoc = await getDoc(doc(db, getTenantCollectionPath('discounts'), id));
			if (discountDoc.exists()) {
				return new Discount({ id: discountDoc.id, ...discountDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching discount:', error);
			throw error;
		}
	}

	static async createDiscount(discountData) {
		try {
			const nextId = await AutoIncrementService.getNextId('discounts', db);
			await setDoc(doc(db, getTenantCollectionPath('discounts'), nextId.toString()), {
				...discountData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating discount:', error);
			throw error;
		}
	}

	static async updateDiscount(id, discountData) {
		try {
			await updateDoc(doc(db, getTenantCollectionPath('discounts'), id), {
				...discountData,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating discount:', error);
			throw error;
		}
	}

	static async deleteDiscount(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('discounts'), id));
		} catch (error) {
			console.error('Error deleting discount:', error);
			throw error;
		}
	}

	// Customers operations
	static async getCustomers({ search = '', perPage = 20 } = {}) {
		try {
			const customersRef = collection(db, getTenantCollectionPath('customers'));
			let q = query(customersRef, orderBy('name'));

			if (search) {
				q = query(
					customersRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			if (perPage) {
				q = query(q, limit(perPage));
			}

			const snapshot = await getDocs(q);
			const customers = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));

			return {
				data: customers,
				totalRecords: customers.length,
			};
		} catch (error) {
			console.error('Error fetching customers:', error);
			throw error;
		}
	}

	static async getCustomer(id) {
		try {
			const customerDoc = await getDoc(doc(db, getTenantCollectionPath('customers'), id));
			if (customerDoc.exists()) {
				return { id: customerDoc.id, ...customerDoc.data() };
			}
			return null;
		} catch (error) {
			console.error('Error fetching customer:', error);
			throw error;
		}
	}

	static async createCustomer(customerData) {
		try {
			const nextId = await AutoIncrementService.getNextId('customers', db);
			await setDoc(doc(db, getTenantCollectionPath('customers'), nextId.toString()), {
				...customerData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating customer:', error);
			throw error;
		}
	}

	static async updateCustomer(id, customerData) {
		try {
			await updateDoc(doc(db, getTenantCollectionPath('customers'), id), {
				...customerData,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating customer:', error);
			throw error;
		}
	}

	static async deleteCustomer(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('customers'), id));
		} catch (error) {
			console.error('Error deleting customer:', error);
			throw error;
		}
	}

	// Options operations
	static async getOptions({ page = 1, per_page = 100, search = '' } = {}) {
		try {
			const optionsRef = collection(db, getTenantCollectionPath('options'));
			let q = query(optionsRef, orderBy('name'));

			if (search) {
				q = query(
					optionsRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const options = snapshot.docs.map((doc) => new Option({ id: doc.id, ...doc.data() }));

			return {
				data: options,
				totalRecords: options.length,
			};
		} catch (error) {
			console.error('Error fetching options:', error);
			throw error;
		}
	}

	static async getOption(id) {
		try {
			const optionDoc = await getDoc(doc(db, getTenantCollectionPath('options'), id));
			if (optionDoc.exists()) {
				return new Option({ id: optionDoc.id, ...optionDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching option:', error);
			throw error;
		}
	}

	static async createOption(optionData) {
		try {
			const nextId = await AutoIncrementService.getNextId('options', db);
			await setDoc(doc(db, getTenantCollectionPath('options'), nextId.toString()), {
				...optionData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating option:', error);
			throw error;
		}
	}

	static async updateOption(id, optionData) {
		try {
			const oldOption = await this.getOption(id);
			await updateDoc(doc(db, getTenantCollectionPath('options'), id), {
				...optionData,
				updatedAt: new Date(),
			});

			if (!oldOption.isSame(new Option({ id, ...optionData }))) {
				OptionFunctions.updateOption(id, optionData);
			}
		} catch (error) {
			console.error('Error updating option:', error);
			throw error;
		}
	}

	static async deleteOption(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('options'), id));
		} catch (error) {
			console.error('Error deleting option:', error);
			throw error;
		}
	}

	// Tags operations
	static async getTags({ page = 1, per_page = 100, search = '' } = {}) {
		try {
			const tagsRef = collection(db, getTenantCollectionPath('tags'));
			let q = query(tagsRef, orderBy('name'));

			if (search) {
				q = query(tagsRef, where('name', '>=', search), where('name', '<=', search + '\uf8ff'));
			}

			const snapshot = await getDocs(q);
			const tags = snapshot.docs.map((doc) => new Tag({ id: doc.id, ...doc.data() }));

			return {
				data: tags,
				totalRecords: tags.length,
			};
		} catch (error) {
			console.error('Error fetching tags:', error);
			throw error;
		}
	}

	static async getTag(id) {
		try {
			const tagDoc = await getDoc(doc(db, getTenantCollectionPath('tags'), id));
			if (tagDoc.exists()) {
				return new Tag({ id: tagDoc.id, ...tagDoc.data() });
			}
			return null;
		} catch (error) {
			console.error('Error fetching tag:', error);
			throw error;
		}
	}

	static async createTag(tagData) {
		try {
			const nextId = await AutoIncrementService.getNextId('tags', db);
			await setDoc(doc(db, getTenantCollectionPath('tags'), nextId.toString()), {
				...tagData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
			return nextId;
		} catch (error) {
			console.error('Error creating tag:', error);
			throw error;
		}
	}

	static async updateTag(id, tagData) {
		try {
			const oldTag = await this.getTag(id);
			await updateDoc(doc(db, getTenantCollectionPath('tags'), id), {
				...tagData,
				updatedAt: new Date(),
			});

			if (!oldTag.isSame(new Tag({ id, ...tagData }))) {
				TagFunctions.updateTag(id, tagData);
			}
		} catch (error) {
			console.error('Error updating tag:', error);
			throw error;
		}
	}

	static async deleteTag(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('tags'), id));
		} catch (error) {
			console.error('Error deleting tag:', error);
			throw error;
		}
	}

	static async syncOption(optionName, optionValues = []) {
		try {
			const optionsRef = collection(db, getTenantCollectionPath('options'));
			const q = query(optionsRef, where('name', '==', optionName));
			const snapshot = await getDocs(q);

			if (snapshot.empty) {
				return String(
					await this.createOption({
						name: optionName,
						values: optionValues,
					}),
				);
			} else {
				// Update existing option with new values if any
				const existingOption = snapshot.docs[0];
				const existingData = existingOption.data();
				const combinedValues = [...new Set([...existingData.values, ...optionValues])];

				if (combinedValues.length > existingData.values.length) {
					await this.updateOption(existingOption.id, {
						...existingData,
						values: combinedValues,
					});
				}

				return existingOption.id;
			}
		} catch (error) {
			console.error('Error ensuring option exists:', error);
			throw error;
		}
	}

	static async ensureTagExists(tagName) {
		try {
			const tagsRef = collection(db, getTenantCollectionPath('tags'));
			const q = query(tagsRef, where('name', '==', tagName));
			const snapshot = await getDocs(q);

			if (snapshot.empty) {
				return String(
					await this.createTag({
						name: tagName,
					}),
				);
			} else {
				return snapshot.docs[0].id;
			}
		} catch (error) {
			console.error('Error ensuring tag exists:', error);
			throw error;
		}
	}

	static async syncTags(tagNames = []) {
		try {
			const syncedTags = [];

			for (const tagName of tagNames) {
				if (tagName && tagName.trim()) {
					const tagId = await this.ensureTagExists(tagName.trim());
					syncedTags.push({
						id: String(tagId),
						name: tagName.trim(),
					});
				}
			}

			return syncedTags;
		} catch (error) {
			console.error('Error syncing tags:', error);
			throw error;
		}
	}

	// Draft orders operations
	static async saveDraft(draftData) {
		try {
			const draftsRef = collection(db, getTenantCollectionPath('order_drafts'));
			const draftDoc = await getDoc(doc(draftsRef, draftData.id));

			if (draftDoc.exists()) {
				await updateDoc(doc(db, getTenantCollectionPath('order_drafts'), draftData.id), {
					...draftData,
					status: 'draft',
					updatedAt: new Date(),
				});
				return draftData.id;
			} else {
				const nextId = await AutoIncrementService.getNextId('order_drafts', db);
				const draftId = nextId.toString();

				await setDoc(doc(db, getTenantCollectionPath('order_drafts'), draftId), {
					...draftData,
					id: draftId,
					status: 'draft',
					createdAt: new Date(),
					updatedAt: new Date(),
				});

				return draftId;
			}
		} catch (error) {
			console.error('Error saving draft:', error);
			throw error;
		}
	}

	static async addConversationCart(customerId, orderData) {
		if (!orderData.items?.length) return;

		const authStore = useAuthStore();
		try {
			const conversationRef = doc(
				db,
				`tenants/${authStore.tenantId}/ai-system-history`,
				customerId,
			);
			const conversationSnap = await getDoc(conversationRef);

			if (conversationSnap.exists()) {
				await updateDoc(conversationRef, {
					cart: {
						items: orderData.items.map((item) => ({
							sku: item.sku,
							name: item.name,
							quantity: item.quantity,
						})),
						remainingFields: [],
					},
				});
			}
		} catch (error) {
			console.error('Error adding conversation cart:', error);
			throw error;
		}
	}

	static async createOrder(orderData) {
		try {
			return await runTransaction(db, async (transaction) => {
				delete orderData.id;
				const finalOrderData = {
					...orderData,
					origin: 'webapp',
					status: orderData.status || 'draft',
					createdAt: new Date(),
					updatedAt: new Date(),
				};

				const orderId = await AutoIncrementService.getNextId('orders', db);

				transaction.set(
					doc(db, getTenantCollectionPath('orders'), String(orderId)),
					finalOrderData,
				);

				try {
					await this.addConversationCart(orderData.customerId, finalOrderData);
				} catch (error) {
					console.error('Error adding conversation cart:', error);
				}

				return { id: orderId, ...finalOrderData };
			});
		} catch (error) {
			console.error('Error creating order:', error);
			throw error;
		}
	}

	static async deleteOrder(id) {
		try {
			const order = await this.getOrder(id);
			if (order.zatcaInvoiceId) {
				throw new Error('You cannot delete an order with a ZATCA invoice');
			}
			await deleteDoc(doc(db, getTenantCollectionPath('orders'), id));
		} catch (error) {
			console.error('Error deleting order:', error);
			throw error;
		}
	}

	static async deleteDraftOrdersBeforeDate(date) {
		try {
			const ordersRef = collection(db, getTenantCollectionPath('orders'));
			const targetDate = new Date(date);
			targetDate.setHours(23, 59, 59, 999);

			const q = query(
				ordersRef,
				where('status', '==', 'draft'),
				where('createdAt', '<=', Timestamp.fromDate(targetDate)),
			);

			const querySnapshot = await getDocs(q);
			const batch = writeBatch(db);

			querySnapshot.forEach((docSnapshot) => {
				batch.delete(docSnapshot.ref);
			});

			await batch.commit();

			return {
				deletedCount: querySnapshot.size,
				message: `Successfully deleted ${querySnapshot.size} draft orders before ${date}`,
			};
		} catch (error) {
			console.error('Error deleting draft orders:', error);
			throw error;
		}
	}

	static async generatePaymentLink(data) {
		try {
			const response = await MyFatoorah.createInvoice(data);
			return response;
		} catch (error) {
			console.error('Error generating payment link:', error);
			throw error;
		}
	}

	// Tax Settings operations
	static async getTaxSettings() {
		try {
			const settingsRef = collection(db, getTenantCollectionPath('settings'));
			const settingsDoc = await getDoc(doc(settingsRef, 'tax'));

			if (settingsDoc.exists()) {
				const data = settingsDoc.data();
				return {
					pricesIncludeTax: data?.pricesIncludeTax || false,
					isTaxable: data?.isTaxable || false,
					taxRate: data?.taxRate || 15,
					taxName: data?.taxName || 'VAT',
					updatedAt: data?.updatedAt || new Date().toISOString(),
				};
			}

			// Return default settings if none exist
			return {
				pricesIncludeTax: false,
				isTaxable: false,
				taxRate: 15,
				taxName: 'VAT',
				updatedAt: new Date().toISOString(),
			};
		} catch (error) {
			console.error('Error fetching tax settings:', error);
			throw error;
		}
	}

	static async updateTaxSettings(taxSettings) {
		try {
			const settingsRef = collection(db, getTenantCollectionPath('settings'));
			await setDoc(doc(settingsRef, 'tax'), {
				...taxSettings,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating tax settings:', error);
			throw error;
		}
	}

	static async getCities() {
		try {
			const citiesRef = collection(db, getTenantCollectionPath('shipping-cities'));
			const citiesSnapshot = await getDocs(citiesRef);

			const cities = [];
			citiesSnapshot.forEach((doc) => {
				const data = doc.data();
				cities.push({
					id: doc.id,
					nameEn: data.nameEn || '',
					nameAr: data.nameAr || '',
				});
			});

			return cities;
		} catch (error) {
			console.error('Error fetching cities:', error);
			throw error;
		}
	}

	static async getCity(id) {
		try {
			const cityRef = doc(db, getTenantCollectionPath('shipping-cities'), id);
			const cityDoc = await getDoc(cityRef);

			if (cityDoc.exists()) {
				const data = cityDoc.data();
				return {
					id: cityDoc.id,
					nameEn: data.nameEn,
					nameAr: data.nameAr || '',
				};
			}

			return null;
		} catch (error) {
			console.error('Error fetching city:', error);
			throw error;
		}
	}

	static async createCity(cityData) {
		try {
			const citiesRef = collection(db, getTenantCollectionPath('shipping-cities'));
			const citiesSnapshot = await getDocs(citiesRef);

			// Find the highest existing ID
			let maxId = 0;
			citiesSnapshot.forEach((doc) => {
				const docId = parseInt(doc.id);
				if (!isNaN(docId) && docId > maxId) {
					maxId = docId;
				}
			});

			// Generate new ID (maxId + 1)
			const newId = (maxId + 1).toString();

			// Create document with the new ID
			await setDoc(doc(citiesRef, newId), {
				nameEn: cityData.nameEn,
				nameAr: cityData.nameAr || '',
				createdAt: new Date(),
				updatedAt: new Date(),
			});

			return newId;
		} catch (error) {
			console.error('Error creating city:', error);
			throw error;
		}
	}

	static async updateCity(id, cityData) {
		try {
			const cityRef = doc(db, getTenantCollectionPath('shipping-cities'), id);
			await updateDoc(cityRef, {
				nameEn: cityData.nameEn,
				nameAr: cityData.nameAr || '',
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating city:', error);
			throw error;
		}
	}

	static async isCityUsedInShippingOptions(cityId) {
		try {
			const shippingOptionsRef = collection(db, getTenantCollectionPath('shipping-options'));
			const shippingOptionsQuery = query(
				shippingOptionsRef,
				where('cityIds', 'array-contains', cityId),
			);
			const snapshot = await getDocs(shippingOptionsQuery);
			return !snapshot.empty;
		} catch (error) {
			console.error('Error checking city usage:', error);
			throw error;
		}
	}

	static async deleteCity(id) {
		try {
			// Check if city is used in any shipping options
			const isUsed = await AgentEcommerceService.isCityUsedInShippingOptions(id);
			if (isUsed) {
				throw new Error(
					'Cannot delete city. It is being used by one or more shipping options.',
				);
			}

			const cityRef = doc(db, getTenantCollectionPath('shipping-cities'), id);
			await deleteDoc(cityRef);
		} catch (error) {
			console.error('Error deleting city:', error);
			throw error;
		}
	}

	// Shipping Types methods
	static async getShippingTypes() {
		try {
			const shippingTypesRef = collection(db, getTenantCollectionPath('shipping-types'));
			const shippingTypesSnapshot = await getDocs(shippingTypesRef);

			const shippingTypes = [];
			shippingTypesSnapshot.forEach((doc) => {
				const data = doc.data();
				shippingTypes.push({
					id: doc.id,
					name: data.name || '',
				});
			});

			return shippingTypes;
		} catch (error) {
			console.error('Error fetching shipping types:', error);
			throw error;
		}
	}

	static async getShippingType(id) {
		try {
			const shippingTypeRef = doc(db, getTenantCollectionPath('shipping-types'), id);
			const shippingTypeDoc = await getDoc(shippingTypeRef);

			if (shippingTypeDoc.exists()) {
				const data = shippingTypeDoc.data();
				return {
					id: shippingTypeDoc.id,
					name: data.name || '',
				};
			}

			return null;
		} catch (error) {
			console.error('Error fetching shipping type:', error);
			throw error;
		}
	}

	static async createShippingType(shippingTypeData) {
		try {
			const shippingTypesRef = collection(db, getTenantCollectionPath('shipping-types'));
			const shippingTypesSnapshot = await getDocs(shippingTypesRef);

			// Find the highest existing ID
			let maxId = 0;
			shippingTypesSnapshot.forEach((doc) => {
				const docId = parseInt(doc.id);
				if (!isNaN(docId) && docId > maxId) {
					maxId = docId;
				}
			});

			// Generate new ID (maxId + 1)
			const newId = (maxId + 1).toString();

			// Create document with the new ID
			await setDoc(doc(shippingTypesRef, newId), {
				name: shippingTypeData.name,
				createdAt: new Date(),
				updatedAt: new Date(),
			});

			return newId;
		} catch (error) {
			console.error('Error creating shipping type:', error);
			throw error;
		}
	}

	static async updateShippingType(id, shippingTypeData) {
		try {
			const shippingTypeRef = doc(db, getTenantCollectionPath('shipping-types'), id);

			// Update shipping type
			await updateDoc(shippingTypeRef, {
				name: shippingTypeData.name,
				updatedAt: new Date(),
			});

			// Cascade update: Update all shipping options that reference this shipping type
			const shippingOptionsRef = collection(db, getTenantCollectionPath('shipping-options'));
			const shippingOptionsQuery = query(shippingOptionsRef, where('shippingTypeId', '==', id));
			const shippingOptionsSnapshot = await getDocs(shippingOptionsQuery);

			if (!shippingOptionsSnapshot.empty) {
				const batch = writeBatch(db);
				shippingOptionsSnapshot.forEach((optionDoc) => {
					const optionRef = doc(db, getTenantCollectionPath('shipping-options'), optionDoc.id);
					batch.update(optionRef, {
						name: shippingTypeData.name,
						updatedAt: new Date(),
					});
				});
				await batch.commit();
			}
		} catch (error) {
			console.error('Error updating shipping type:', error);
			throw error;
		}
	}

	static async isShippingTypeUsedInShippingOptions(shippingTypeId) {
		try {
			const shippingOptionsRef = collection(db, getTenantCollectionPath('shipping-options'));
			const shippingOptionsQuery = query(
				shippingOptionsRef,
				where('shippingTypeId', '==', shippingTypeId),
			);
			const snapshot = await getDocs(shippingOptionsQuery);
			return !snapshot.empty;
		} catch (error) {
			console.error('Error checking shipping type usage:', error);
			throw error;
		}
	}

	static async deleteShippingType(id) {
		try {
			// Check if shipping type is used in any shipping options
			const isUsed = await AgentEcommerceService.isShippingTypeUsedInShippingOptions(id);
			if (isUsed) {
				throw new Error(
					'Cannot delete shipping type. It is being used by one or more shipping options.',
				);
			}

			const shippingTypeRef = doc(db, getTenantCollectionPath('shipping-types'), id);
			await deleteDoc(shippingTypeRef);
		} catch (error) {
			console.error('Error deleting shipping type:', error);
			throw error;
		}
	}

	// Shipping Options methods
	static async getShippingOptions() {
		try {
			const shippingOptionsRef = collection(db, getTenantCollectionPath('shipping-options'));
			const shippingOptionsSnapshot = await getDocs(shippingOptionsRef);

			const shippingOptions = [];
			shippingOptionsSnapshot.forEach((doc) => {
				shippingOptions.push({
					id: doc.id,
					...doc.data(),
				});
			});

			return shippingOptions;
		} catch (error) {
			console.error('Error fetching shipping options:', error);
			throw error;
		}
	}

	static async getShippingOption(id) {
		try {
			const shippingOptionRef = doc(db, getTenantCollectionPath('shipping-options'), id);
			const shippingOptionDoc = await getDoc(shippingOptionRef);

			if (shippingOptionDoc.exists()) {
				return {
					id: shippingOptionDoc.id,
					...shippingOptionDoc.data(),
				};
			}

			return null;
		} catch (error) {
			console.error('Error fetching shipping option:', error);
			throw error;
		}
	}

	static async createShippingOption(shippingOptionData) {
		try {
			const shippingOptionsRef = collection(db, getTenantCollectionPath('shipping-options'));
			const shippingOptionsSnapshot = await getDocs(shippingOptionsRef);

			// Find the highest existing ID
			let maxId = 0;
			shippingOptionsSnapshot.forEach((doc) => {
				const docId = parseInt(doc.id);
				if (!isNaN(docId) && docId > maxId) {
					maxId = docId;
				}
			});

			// Generate new ID (maxId + 1)
			const newId = (maxId + 1).toString();

			// Create document with the new ID
			await setDoc(doc(shippingOptionsRef, newId), {
				...shippingOptionData,
				createdAt: new Date(),
				updatedAt: new Date(),
			});

			return newId;
		} catch (error) {
			console.error('Error creating shipping option:', error);
			throw error;
		}
	}

	static async updateShippingOption(id, shippingOptionData) {
		try {
			const shippingOptionRef = doc(db, getTenantCollectionPath('shipping-options'), id);
			await updateDoc(shippingOptionRef, {
				...shippingOptionData,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.error('Error updating shipping option:', error);
			throw error;
		}
	}

	static async deleteShippingOption(id) {
		try {
			const shippingOptionRef = doc(db, getTenantCollectionPath('shipping-options'), id);
			await deleteDoc(shippingOptionRef);
		} catch (error) {
			console.error('Error deleting shipping option:', error);
			throw error;
		}
	}

	static async getInvoiceSettings() {
		try {
			const settingsRef = doc(db, getTenantCollectionPath('settings'), 'invoices');
			const docSnap = await getDoc(settingsRef);

			if (docSnap.exists()) {
				const data = docSnap.data();
				return {
					companyName: data?.companyName || '',
					country: data?.country || '',
					city: data?.city || '',
					address: data?.address || '',
					taxNumber: data?.taxNumber || '',
					companyRegistrationNumber: data?.companyRegistrationNumber || '',
					companyRegistrationExpiryDate: data?.companyRegistrationExpiryDate || null,
					autoGenerateInvoices: data?.autoGenerateInvoices ?? true,
					invoiceNumbering: {
						prefix: data?.invoiceNumbering?.prefix || 'INV',
						charsCount: data?.invoiceNumbering?.charsCount || 6,
						startNumber: data?.invoiceNumbering?.startNumber || 1,
					},
					returnInvoiceNumbering: {
						prefix: data?.returnInvoiceNumbering?.prefix || 'RET',
						charsCount: data?.returnInvoiceNumbering?.charsCount || 6,
						startNumber: data?.returnInvoiceNumbering?.startNumber || 1,
					},
					logo: data?.logo || null,
					updatedAt: data?.updatedAt || new Date().toISOString(),
				};
			}

			// Return default settings if none exist
			return new InvoiceSettings();
		} catch (error) {
			console.error('Error fetching invoice settings:', error);
			throw error;
		}
	}

	static async updateInvoiceSettings(invoiceSettings) {
		try {
			const settingsRef = doc(db, getTenantCollectionPath('settings'), 'invoices');
			await setDoc(settingsRef, invoiceSettings.toFirestore());
		} catch (error) {
			console.error('Error updating invoice settings:', error);
			throw error;
		}
	}

	static async getAgentSettings() {
		const authStore = useAuthStore();
		try {
			const settingsRef = doc(db, 'tenants', `${authStore.tenantId}`);
			const docSnap = await getDoc(settingsRef);

			if (docSnap.exists()) {
				const data = docSnap.data();
				return new AgentSettings(data.agentConfigurations || {});
			}

			// Return default settings if none exist
			return new AgentSettings();
		} catch (error) {
			console.error('Error fetching agent settings:', error);
			throw error;
		}
	}

	static async updateAgentSettings(agentSettings) {
		try {
			const authStore = useAuthStore();
			const settingsRef = doc(db, 'tenants', `${authStore.tenantId}`);
			await updateDoc(settingsRef, { agentConfigurations: agentSettings.toFirestore() });
		} catch (error) {
			console.error('Error updating agent settings:', error);
			throw error;
		}
	}

	static async generateZatcaInvoice(orderId) {
		try {
			const result = await callFirebaseFunction('zatcaaGenerateInvoice', { orderId });
			return result;
		} catch (error) {
			console.error('Error generating ZATCA invoice:', error);
			throw error;
		}
	}

	static async getZatcaInvoice(invoiceId) {
		try {
			const invoiceRef = doc(db, getTenantCollectionPath('zatca-invoices'), invoiceId);
			const invoiceSnap = await getDoc(invoiceRef);

			if (invoiceSnap.exists()) {
				return { id: invoiceSnap.id, ...invoiceSnap.data() };
			}
			return null;
		} catch (error) {
			console.error('Error fetching ZATCA invoice:', error);
			throw error;
		}
	}

	static async getMosques({ page = 1, per_page = 10, search = '' } = {}) {
		try {
			const mosquesRef = collection(db, getTenantCollectionPath('mosques'));
			let q = query(mosquesRef, orderBy('name'));

			if (search) {
				q = query(
					mosquesRef,
					where('name', '>=', search),
					where('name', '<=', search + '\uf8ff'),
				);
			}

			const snapshot = await getDocs(q);
			const mosques = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));

			return {
				data: mosques,
				totalRecords: mosques.length,
			};
		} catch (error) {
			console.error('Error fetching mosques:', error);
			throw error;
		}
	}

	static async getMosque(id) {
		try {
			const mosqueDoc = await getDoc(doc(db, getTenantCollectionPath('mosques'), id));
			if (mosqueDoc.exists()) {
				return { id: mosqueDoc.id, ...mosqueDoc.data() };
			}
			return null;
		} catch (error) {
			console.error('Error fetching mosque:', error);
			throw error;
		}
	}

	static async createMosque(mosqueData) {
		try {
			const mosquesRef = collection(db, getTenantCollectionPath('mosques'));
			const docRef = await addDoc(mosquesRef, {
				...mosqueData,
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			});
			return docRef.id;
		} catch (error) {
			console.error('Error creating mosque:', error);
			throw error;
		}
	}

	static async updateMosque(id, mosqueData) {
		try {
			await updateDoc(doc(db, getTenantCollectionPath('mosques'), id), {
				...mosqueData,
				updatedAt: new Date().toISOString(),
			});
		} catch (error) {
			console.error('Error updating mosque:', error);
			throw error;
		}
	}

	static async deleteMosque(id) {
		try {
			await deleteDoc(doc(db, getTenantCollectionPath('mosques'), id));
		} catch (error) {
			console.error('Error deleting mosque:', error);
			throw error;
		}
	}

	static async cancelAllProductSubscriptions(productId) {
		try {
			const response = await callFirebaseFunction('cancelAllProductSubscriptions', {
				productId,
			});
			return response;
		} catch (error) {
			console.error('Error canceling all product subscriptions:', error);
			throw error;
		}
	}

	static async bulkUploadProducts(fileUrl, templateType) {
		try {
			const response = await callFirebaseFunction('ecommerceStartBulkUpload', {
				fileUrl,
				templateType,
			});
			return response.data;
		} catch (error) {
			console.error('Error bulk uploading products:', error);
			throw error;
		}
	}

	static async seedDemoCatalog(industry) {
		try {
			const response = await callFirebaseFunction('ecommerceSeedDemoCatalog', {
				industry,
			});
			return response.data;
		} catch (error) {
			console.error('Error seeding demo catalog:', error);
			throw error;
		}
	}
}
